import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { newKey, todayISO, fmtMoney } from "../../lib/format";
import { Modal, INP, LBL } from "./shared";

const PARTY = { company: "", first_name: "", last_name: "", address: "", city: "", state: "", zip_code: "", parish: "", country: "", phone: "", alt_phone: "", email: "" };
const BLANK_ITEM = { qty: "1", description: "", length: "", width: "", height: "", weight_lbs: "", remarks: "" };
const NUM = ["freight_charge", "pickup_charge", "other_charges", "amount_paid"];
const OPTIONAL = { door_to_door: null, brokerage: null, cargo_type: null, payment_terms: null, hazardous: null, examined: null, insurance_declined: false };
const str = (v) => (v == null ? "" : String(v));

function toForm(src) {
  const s = src || {};
  return {
    date: todayISO(), mode: "Ocean", bl_awb_no: "", origin: "", destination: "", declared_value: "", delivered_by: "",
    receiving_clerk: "", received_time: "", shipper_print_name: "", currency: "CAD", payment_method: "", notes: "", quote_number: "",
    ...OPTIONAL,
    ...Object.fromEntries(Object.entries(s).filter(([, v]) => v != null)),
    declared_value: str(s.declared_value),
    shipper: { ...PARTY, ...Object.fromEntries(Object.entries(s.shipper || {}).map(([k, v]) => [k, str(v)])) },
    consignee: { ...PARTY, ...Object.fromEntries(Object.entries(s.consignee || {}).map(([k, v]) => [k, str(v)])) },
    ...Object.fromEntries(NUM.map((k) => [k, s[k] != null ? String(s[k]) : "0"])),
    items: (s.items?.length ? s.items : [BLANK_ITEM]).map((it) => ({ ...BLANK_ITEM, ...Object.fromEntries(Object.entries(it).map(([k, v]) => [k, str(v)])), key: newKey() })),
  };
}

const num = (v) => (v === "" || v == null ? null : Number(v));

function toPayload(f) {
  return {
    ...f,
    declared_value: num(f.declared_value),
    ...Object.fromEntries(NUM.map((k) => [k, num(f[k]) || 0])),
    items: f.items.map(({ key, cubic, ...it }) => ({
      ...it, qty: parseInt(it.qty, 10) || 1, length: num(it.length), width: num(it.width), height: num(it.height), weight_lbs: num(it.weight_lbs),
    })),
  };
}

function Choice({ label, value, options, onChange, testId }) {
  return (
    <div>
      <div className={LBL}>{label}</div>
      <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid={testId}>
        {options.map(([v, l]) => (
          <button type="button" key={String(v)} onClick={() => onChange(value === v ? null : v)} data-testid={`${testId}-${String(v)}`}
            className={`px-3 py-1.5 text-xs font-semibold border-r last:border-r-0 border-slate-200 transition-colors ${value === v ? "bg-[#1E2A78] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function PartyEditor({ title, party, onChange, consignee, prefix }) {
  const fields = [["company", "Company", 2], ["first_name", "First name", 1], ["last_name", "Last name", 1], ["address", "Address", 2], ["city", "City", 1],
    ...(consignee ? [["parish", "Parish", 1]] : [["state", "State / Prov.", 1], ["zip_code", "ZIP / Postal", 1]]),
    ["country", "Country", consignee ? 2 : 1], ["phone", "Telephone 1", 1], ["alt_phone", "Alternate telephone", 1], ["email", "Email", 2]];
  return (
    <div className="p-4 bg-slate-50 rounded-sm">
      <div className="text-xs font-bold uppercase tracking-wider text-[#1E2A78] mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-2">
        {fields.map(([k, l, span]) => (
          <div key={k} className={span === 2 ? "col-span-2" : ""}>
            <label className={LBL}>{l}</label>
            <input className={`${INP} uppercase`} value={party[k]} onChange={(e) => onChange({ ...party, [k]: e.target.value })} data-testid={`rf-${prefix}-${k}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemsEditor({ items, setItems }) {
  const upd = (key, k, v) => setItems(items.map((i) => (i.key === key ? { ...i, [k]: v } : i)));
  const cols = [["qty", "Pkgs", "col-span-1", "number"], ["description", "Description of packages & goods", "col-span-4"], ["length", "L (in)", "col-span-1", "number"],
    ["width", "W (in)", "col-span-1", "number"], ["height", "H (in)", "col-span-1", "number"], ["weight_lbs", "Weight (lbs)", "col-span-1", "number"], ["remarks", "Remarks & seal no.", "col-span-2"]];
  return (
    <div className="space-y-2" data-testid="receipt-items-editor">
      <div className="grid grid-cols-12 gap-2">{cols.map(([k, l, c]) => <div key={k} className={`${c} ${LBL} mb-0`}>{l}</div>)}</div>
      {items.map((it, idx) => (
        <div key={it.key} className="grid grid-cols-12 gap-2 items-center">
          {cols.map(([k, , c, type]) => (
            <input key={k} type={type || "text"} step="any" min={type ? "0" : undefined} className={`${INP} ${c} px-2`} value={it[k]} onChange={(e) => upd(it.key, k, e.target.value)} data-testid={`ri-${k}-${idx}`} />
          ))}
          <button type="button" disabled={items.length === 1} onClick={() => setItems(items.filter((x) => x.key !== it.key))} className="col-span-1 justify-self-center text-slate-400 hover:text-red-600 disabled:opacity-30" data-testid={`ri-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => setItems([...items, { ...BLANK_ITEM, key: newKey() }])} className="text-xs text-[#1E2A78] font-semibold inline-flex items-center gap-1 hover:underline" data-testid="ri-add-item"><Plus className="h-3.5 w-3.5" /> Add line</button>
    </div>
  );
}

function F({ label, k, f, set, type = "text", span = "", list }) {
  return (
    <div className={span}>
      <label className={LBL}>{label}</label>
      <input type={type} step={type === "number" ? "0.01" : undefined} list={list} className={INP} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`rf-${k}`} />
    </div>
  );
}

function Section({ title, children }) {
  return <div><div className="text-xs font-bold uppercase tracking-wider text-[#1E2A78] mb-2">{title}</div>{children}</div>;
}

function DeclarationEditor({ f, set, p }) {
  return (
    <div className="grid sm:grid-cols-3 gap-4 p-4 border border-slate-200 rounded-sm">
      <F label="Declared value for insurance" k="declared_value" type="number" {...p} />
      <label className="flex items-end gap-2 text-sm pb-2"><input type="checkbox" checked={f.insurance_declined} onChange={(e) => set("insurance_declined", e.target.checked)} className="h-4 w-4 accent-[#1E2A78]" data-testid="rf-insurance_declined" /> Insurance declined</label>
      <Choice label="Hazardous?" value={f.hazardous} onChange={(v) => set("hazardous", v)} options={[[true, "Hazardous"], [false, "Non-hazardous"]]} testId="rf-hazardous" />
      <Choice label="Door to door" value={f.door_to_door} onChange={(v) => set("door_to_door", v)} options={[[true, "Yes"], [false, "No"]]} testId="rf-door_to_door" />
      <Choice label="Brokerage" value={f.brokerage} onChange={(v) => set("brokerage", v)} options={[[true, "Yes"], [false, "No"]]} testId="rf-brokerage" />
      <Choice label="Cargo type" value={f.cargo_type} onChange={(v) => set("cargo_type", v)} options={[["commercial", "Commercial"], ["personal", "Personal"]]} testId="rf-cargo_type" />
      <div className="sm:col-span-3"><Choice label="Payment terms" value={f.payment_terms} onChange={(v) => set("payment_terms", v)} options={[["prepaid", "Pre-paid"], ["freight_collect", "Freight collect"], ["on_account", "On account"]]} testId="rf-payment_terms" /></div>
    </div>
  );
}

export function ReceiptFormModal({ receipt, prefill, nextNumber, onClose, onSuccess }) {
  const [f, setF] = useState(() => toForm(receipt || prefill));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const p = { f, set };
  const total = NUM.slice(0, 3).reduce((a, k) => a + (Number(f[k]) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = receipt ? await api.put(`/receipts/${receipt.id}`, toPayload(f)) : await api.post("/receipts", toPayload(f));
      toast.success(`Warehouse receipt ${data.receipt_number} saved`);
      onSuccess(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal wide title={receipt ? `Edit Receipt ${receipt.receipt_number}` : `New Warehouse Receipt${nextNumber ? ` · ${nextNumber}` : ""}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-5" data-testid="receipt-form">
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div><label className={LBL}>Date *</label><input type="date" required className={INP} value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="rf-date" /></div>
          <Choice label="Circle one" value={f.mode} onChange={(v) => set("mode", v || "Ocean")} options={[["Ocean", "Ocean"], ["Air", "Air"], ["Express", "Express"]]} testId="rf-mode" />
          <F label="BL / AWB no." k="bl_awb_no" {...p} />
          <F label="Origin" k="origin" {...p} />
          <F label="Destination" k="destination" {...p} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <PartyEditor title="From: (Shipper information)" party={f.shipper} onChange={(v) => set("shipper", v)} prefix="shipper" />
          <PartyEditor title="To: (Consignee information)" party={f.consignee} onChange={(v) => set("consignee", v)} prefix="consignee" consignee />
        </div>
        <Section title="Packages & goods (cubic feet calculated automatically)"><ItemsEditor items={f.items} setItems={(items) => set("items", items)} /></Section>
        <Section title="Declaration"><DeclarationEditor f={f} set={set} p={p} /></Section>
        <Section title="Receiving">
          <div className="grid sm:grid-cols-5 gap-3 items-end">
            <F label="Delivered by" k="delivered_by" {...p} />
            <F label="Receiving clerk" k="receiving_clerk" {...p} />
            <F label="Time" k="received_time" type="time" {...p} />
            <Choice label="Examined" value={f.examined} onChange={(v) => set("examined", v)} options={[[true, "Yes"], [false, "No"]]} testId="rf-examined" />
            <F label="Shipper print name" k="shipper_print_name" {...p} />
          </div>
          <div className="mt-3"><F label="Notes (printed on receipt)" k="notes" {...p} /></div>
        </Section>
        <Section title="Office use only (not printed)">
          <div className="grid sm:grid-cols-6 gap-3">
            <div><label className={LBL}>Currency</label><select className={INP} value={f.currency} onChange={(e) => set("currency", e.target.value)} data-testid="rf-currency"><option>CAD</option><option>USD</option></select></div>
            <F label="Freight" k="freight_charge" type="number" {...p} />
            <F label="Pickup" k="pickup_charge" type="number" {...p} />
            <F label="Other" k="other_charges" type="number" {...p} />
            <F label="Amount paid" k="amount_paid" type="number" {...p} />
            <F label="Paid by" k="payment_method" list="pay-methods" {...p} />
            <datalist id="pay-methods"><option>Cash</option><option>Interac e-Transfer</option><option>Debit</option><option>Credit Card</option><option>Zelle</option></datalist>
          </div>
          <div className="text-sm text-slate-600 mt-2" data-testid="rf-totals">Total charges: <b>{fmtMoney(total, f.currency)}</b> · Balance due: <b>{fmtMoney(total - (Number(f.amount_paid) || 0), f.currency)}</b></div>
        </Section>
        <button type="submit" disabled={saving} className="w-full px-4 py-2.5 bg-[#1E2A78] text-white font-semibold rounded-sm hover:bg-[#17205E] disabled:opacity-60" data-testid="rf-submit">
          {saving ? "Saving..." : receipt ? "Save Changes" : "Issue Receipt"}
        </button>
      </form>
    </Modal>
  );
}
